import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, ShoppingCart, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setMobileAboutOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  const navLinkClass = 'text-stone-800 dark:text-white';
  const dropdownLinkClass = 'text-stone-600 dark:text-stone-300';
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-brand-muted';
  const {
    totalItems,
    totalPrice,
    toggleCart
  } = useCart();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  const goToProduct = useCallback((categoryName: string, productIndex: number) => {
    const params = new URLSearchParams({
      cat: categoryName,
      prod: String(productIndex)
    });
    if (location.pathname !== '/') {
      navigate(`/?${params.toString()}#materials`);
    } else {
      window.history.replaceState(null, '', `/?${params.toString()}#materials`);
      document.getElementById('materials')?.scrollIntoView({
        behavior: 'smooth'
      });
      // Dispatch event so Services picks up the change
      window.dispatchEvent(new Event('materialsNav'));
    }
  }, [location.pathname, navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? 'bg-white dark:bg-[#070707] backdrop-blur-md shadow-lg border-stone-200 dark:border-white/20' : 'bg-white/60 dark:bg-[#070707]/50 border-stone-200/50 dark:border-white/20'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? '56px' : isMobileNav ? '80px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-643">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-644">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? '56px' : isMobileNav ? '80px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-645">
          <div className="flex-shrink-0 flex items-center" data-id="element-646">
            <Link to="/" onClick={resetMaterials} className="relative" data-id="element-647">
              <motion.img src="https://cdn.magicpatterns.com/uploads/3C1xrkGTfVHurUv4Wdvj7g/logo_253.png" alt="253Gravel" className="h-10 max-sm:h-9 w-auto dark:brightness-100 brightness-0" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 2,
              ease: 'easeOut'
            }} data-id="element-648" />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8 font-nav" data-id="element-649">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-650">
              <button className={`flex items-center ${isContractorPage ? 'bg-brand-orange hover:bg-brand-orange/90' : 'bg-[#0a9e5a] hover:bg-[#0bb868]'} text-white font-bold text-sm px-4 py-2 rounded-full transition-colors shadow-sm`} data-id="element-651">
                Shop <ChevronDown className="w-4 h-4 ml-1" data-id="element-652" />
              </button>

              <AnimatePresence data-id="element-653">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-654">
                    <div className="grid grid-cols-3 gap-5" data-id="element-655">
                      {categories.map(cat => <div key={cat.name} data-id="element-656">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-657">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-658">
                            {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(cat.name, prodIdx);
                      }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors text-left w-full`} data-id="element-659">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 pt-4 border-t border-stone-200 dark:border-white/10" data-id="element-660">
                      <Link to="/subcontractors" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors group" data-id="element-661">
                        <div data-id="element-662">
                          <span className="block text-brand-orange font-bold text-sm" data-id="element-663">
                            Contractor Ordering
                          </span>
                          <span className="block text-stone-600 dark:text-stone-200 text-xs mt-0.5" data-id="element-664">
                            Bulk pricing & volume discounts
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-brand-orange opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-665" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button onClick={() => {
            collapseCard();
            setIsOpen(false);
            if (location.pathname !== '/') {
              navigate('/');
              setTimeout(() => {
                document.getElementById('materials')?.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new Event('materialsNav'));
              }, 300);
            } else {
              document.getElementById('materials')?.scrollIntoView({
                behavior: 'smooth'
              });
              window.dispatchEvent(new Event('materialsNav'));
            }
          }} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-666">
              Materials
            </button>
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-667">
              <button className={`flex items-center ${navLinkClass} hover:text-brand-orange font-medium transition-colors py-2`} data-id="element-668">
                Resources <ChevronDown className="w-4 h-4 ml-1" data-id="element-669" />
              </button>
              <AnimatePresence data-id="element-670">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-671">
                    <Link to="/help" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-672">
                      Quiz & Calculator
                    </Link>
                    <Link to="/diy-help" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-673">
                      DIY Driveway
                    </Link>
                    <Link to="/compare" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-674">
                      Compare Materials
                    </Link>
                    <Link to="/delivery-prep" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-675">
                      Delivery Prep Guide
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/delivery-zones" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-676">
              Delivery Zones
            </Link>
            <div className="relative" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)} data-id="element-677">
              <button className={`flex items-center ${navLinkClass} hover:text-brand-orange font-medium transition-colors py-2`} data-id="element-678">
                Info <ChevronDown className="w-4 h-4 ml-1" data-id="element-679" />
              </button>
              <AnimatePresence data-id="element-680">
                {isAboutOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-681">
                    <Link to="/about" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-682">
                      About Us
                    </Link>
                    <Link to="/about#gallery" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                  setTimeout(() => {
                    const el = document.getElementById('gallery');
                    if (el) {
                      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                      const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                      window.scrollTo({
                        top,
                        behavior: 'smooth'
                      });
                    }
                  }, 400);
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-683">
                      Project Gallery
                    </Link>
                    <Link to="/subcontractors" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-684">
                      Contractors Zone
                    </Link>
                    <Link to="/#faq" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-685">
                      Da FAQs
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-686">
              Contact
            </Link>

            <div className="flex items-center space-x-4 border-l border-stone-300 dark:border-stone-600 pl-6" data-id="element-687">
              <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-688" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className="relative flex items-center gap-1.5 p-2 text-stone-800 dark:text-white hover:text-brand-orange transition-colors" aria-label="Open cart" data-id="element-689">
                <ShoppingCart className="w-5 h-5" data-id="element-690" />
                <AnimatePresence data-id="element-691">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute top-0 right-0 -mt-1 -mr-1 flex items-center justify-center w-4 h-4 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-692">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
                {totalItems > 0 && <span className="text-sm font-bold text-stone-800 dark:text-white ml-0.5" data-id="element-693">
                    ${totalPrice.toLocaleString()}
                  </span>}
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-4" data-id="element-694">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-695" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className="relative flex items-center gap-1 p-2 text-stone-800 dark:text-white hover:text-brand-orange transition-colors" aria-label="Open cart" data-id="element-696">
              <ShoppingCart className="w-5 h-5" data-id="element-697" />
              <AnimatePresence data-id="element-698">
                {totalItems > 0 && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} exit={{
                scale: 0
              }} className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-699">
                    {totalItems}
                  </motion.span>}
              </AnimatePresence>
              {totalItems > 0 && <span className="text-xs font-bold text-stone-800 dark:text-white" data-id="element-700">
                  ${totalPrice.toLocaleString()}
                </span>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" data-id="element-701">
              {isOpen ? <X className="h-6 w-6" data-id="element-702" /> : <Menu className="h-6 w-6" data-id="element-703" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-704">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-705" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-706">
              <div className="flex justify-between items-center p-6" data-id="element-707">
                <img src="https://cdn.magicpatterns.com/uploads/pv38EKqBFfU3b2ryMtdaxz/NEW_GRAVEL_LOGO_bw.png" alt="253Gravel" className="h-10 w-auto" style={{
              filter: isDark ? undefined : 'brightness(0)'
            }} data-id="element-708" />
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" data-id="element-709">
                  <X className="w-6 h-6" data-id="element-710" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-711">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-712">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-713">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-714">
                      <ChevronDown className="w-5 h-5" data-id="element-715" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-716">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-717">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-718">
                          {categories.map(cat => <div key={cat.name} data-id="element-719">
                              <p className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-2 text-right" data-id="element-720">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-721">
                                {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(cat.name, prodIdx);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-722">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 pt-3 border-t border-stone-200 dark:border-white/10" data-id="element-723">
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors" data-id="element-724">
                            <div className="text-right flex-1" data-id="element-725">
                              <span className="block text-brand-orange font-bold text-sm" data-id="element-726">
                                Contractor Ordering
                              </span>
                              <span className="block text-stone-500 dark:text-stone-300 text-xs mt-0.5" data-id="element-727">
                                Bulk pricing & volume discounts
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-brand-orange ml-3" data-id="element-728" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-729">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-730">
                    Resources
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-731" />
                  </button>
                  <AnimatePresence data-id="element-732">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-733">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-734">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-735">
                            Quiz & Calculator
                          </Link>
                          <Link to="/diy-help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-736">
                            DIY Driveway
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-737">
                            Compare Materials
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-738">
                            Delivery Prep Guide
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-739">
                  Delivery Zones
                </Link>
                <div data-id="element-740">
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-741">
                    Info
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} data-id="element-742" />
                  </button>
                  <AnimatePresence data-id="element-743">
                    {mobileAboutOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-744">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-745">
                          <Link to="/about" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-746">
                            About Us
                          </Link>
                          <Link to="/about#gallery" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                      setTimeout(() => {
                        const el = document.getElementById('gallery');
                        if (el) {
                          const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                          const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                          window.scrollTo({
                            top,
                            behavior: 'smooth'
                          });
                        }
                      }, 400);
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-747">
                            Project Gallery
                          </Link>
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-748">
                            Contractors Zone
                          </Link>
                          <Link to="/#faq" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-749">
                            Da FAQs
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-750">
                  Contact
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}